import { useMemo, useState } from "react";
import {
  Truck,
  Package,
  CheckCircle,
  Clock,
  AlertCircle,
  Play,
  XCircle,
  Route,
  Building2,
  TrendingUp,
  Filter,
  X,
  ChevronDown,
} from "lucide-react";

const ViajesDashboard = ({ viajes = [], userRole = "", onFilterChange }) => {
  const [activeFilter, setActiveFilter] = useState(null);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [tipoOperacion, setTipoOperacion] = useState("all");

  const metrics = useMemo(() => {
    const total = viajes.length;
    const pendiente = viajes.filter((v) => v.estado === "pendiente").length;
    const enCurso = viajes.filter((v) => v.estado === "en_curso").length;
    const completado = viajes.filter((v) => v.estado === "completado").length;

    // ✅ CAMBIADO: usar totalTramos en vez de tramos?.length
    const conTramos = viajes.filter((v) => v.totalTramos > 0).length;
    const sinTramos = viajes.filter((v) => !v.totalTramos).length;

    const exportacion = viajes.filter(
      (v) => v.tipoOperacion === "exportacion",
    ).length;
    const importacion = viajes.filter(
      (v) => v.tipoOperacion === "importacion",
    ).length;

    // ✅ CAMBIADO: usar vehiculoPlaca en vez de vehiculo?.placa
    const vehiculosEnRuta = new Set(
      viajes
        .filter((v) => v.estado === "en_curso" && v.vehiculoPlaca)
        .map((v) => v.vehiculoPlaca),
    ).size;

    const vehiculosTotales = new Set(
      viajes.filter((v) => v.vehiculoPlaca).map((v) => v.vehiculoPlaca),
    ).size;

    const tasaCompletado =
      total > 0 ? Math.round((completado / total) * 100) : 0;

    return {
      total,
      pendiente,
      enCurso,
      completado,
      conTramos,
      sinTramos,
      exportacion,
      importacion,
      vehiculosEnRuta,
      vehiculosTotales,
      tasaCompletado,
    };
  }, [viajes]);

  const applyFilter = (filterKey) => {
    const next = activeFilter === filterKey ? null : filterKey;
    setActiveFilter(next);

    let filtered = [...viajes];
    if (tipoOperacion !== "all")
      filtered = filtered.filter((v) => v.tipoOperacion === tipoOperacion);

    if (next) {
      switch (next) {
        case "pendiente":
        case "en_curso":
        case "completado":
          filtered = filtered.filter((v) => v.estado === next);
          break;
        // ✅ CAMBIADO: usar totalTramos
        case "sin_tramos":
          filtered = filtered.filter((v) => !v.totalTramos);
          break;
        case "con_tramos":
          filtered = filtered.filter((v) => v.totalTramos > 0);
          break;
        // ✅ CAMBIADO: usar vehiculoPlaca
        case "en_ruta":
          filtered = filtered.filter(
            (v) => v.estado === "en_curso" && v.vehiculoPlaca,
          );
          break;
        default:
          break;
      }
    }

    onFilterChange?.(filtered);
  };

  const applyTipoOp = (tipo) => {
    setTipoOperacion(tipo);
    let filtered = [...viajes];
    if (tipo !== "all")
      filtered = filtered.filter((v) => v.tipoOperacion === tipo);

    if (activeFilter) {
      switch (activeFilter) {
        case "pendiente":
        case "en_curso":
        case "completado":
          filtered = filtered.filter((v) => v.estado === activeFilter);
          break;
        // ✅ CAMBIADO: usar totalTramos
        case "sin_tramos":
          filtered = filtered.filter((v) => !v.totalTramos);
          break;
        case "con_tramos":
          filtered = filtered.filter((v) => v.totalTramos > 0);
          break;
        default:
          break;
      }
    }

    onFilterChange?.(filtered);
  };

  const clearAll = () => {
    setActiveFilter(null);
    setTipoOperacion("all");
    onFilterChange?.([...viajes]);
  };

  const hasActiveFilters = activeFilter || tipoOperacion !== "all";

  const kpis = [
    {
      key: "pendiente",
      label: "Pendientes",
      value: metrics.pendiente,
      icon: <Clock className="h-3.5 w-3.5" />,
      color: "text-blue-600",
      bg: "bg-blue-50",
      activeBg: "bg-blue-600",
      border: "border-blue-400",
      bar: "bg-blue-500",
      pct: metrics.total > 0 ? (metrics.pendiente / metrics.total) * 100 : 0,
    },
    {
      key: "en_curso",
      label: "En Curso",
      value: metrics.enCurso,
      icon: <Play className="h-3.5 w-3.5" />,
      color: "text-amber-600",
      bg: "bg-amber-50",
      activeBg: "bg-amber-600",
      border: "border-amber-400",
      bar: "bg-amber-500",
      pct: metrics.total > 0 ? (metrics.enCurso / metrics.total) * 100 : 0,
    },
    {
      key: "completado",
      label: "Completados",
      value: metrics.completado,
      icon: <CheckCircle className="h-3.5 w-3.5" />,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      activeBg: "bg-emerald-600",
      border: "border-emerald-400",
      bar: "bg-emerald-500",
      pct: metrics.total > 0 ? (metrics.completado / metrics.total) * 100 : 0,
    },
  ];

  return (
    <div className="mb-4 space-y-2">
      {/* ── Header con métricas principales ── */}
      <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-xl border border-gray-200 p-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Métricas izquierda */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Total */}
            <div className="flex items-center gap-1.5">
              <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm">
                <Package className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900 leading-none">
                  {metrics.total}
                </div>
                <div className="text-[10px] text-gray-500">Viajes</div>
              </div>
            </div>

            <div className="h-6 w-px bg-gray-300" />

            {/* Tasa completado */}
            <div className="flex items-center gap-1.5">
              <div className="p-1.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg shadow-sm">
                <TrendingUp className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-emerald-600 leading-none">
                  {metrics.tasaCompletado}%
                </div>
                <div className="text-[10px] text-gray-500">Completados</div>
              </div>
            </div>

            {/* Flota en ruta */}
            {(userRole === "transportista" ||
              userRole === "operador_logistico") && (
              <>
                <div className="h-6 w-px bg-gray-300" />
                <button
                  onClick={() => applyFilter("en_ruta")}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all cursor-pointer ${
                    activeFilter === "en_ruta"
                      ? "bg-orange-100 ring-2 ring-orange-500"
                      : "hover:bg-orange-50"
                  }`}
                >
                  <Truck
                    className={`h-3.5 w-3.5 ${activeFilter === "en_ruta" ? "text-orange-600" : "text-orange-500"}`}
                  />
                  <div>
                    <div className="text-xs font-bold text-orange-600 leading-none">
                      {metrics.vehiculosEnRuta}/{metrics.vehiculosTotales}
                    </div>
                    <div className="text-[10px] text-orange-600/70">
                      En ruta
                    </div>
                  </div>
                </button>
              </>
            )}

            {/* Alerta sin tramos */}
            {metrics.sinTramos > 0 && userRole !== "conductor" && (
              <>
                <div className="h-6 w-px bg-gray-300" />
                <button
                  onClick={() => applyFilter("sin_tramos")}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all cursor-pointer ${
                    activeFilter === "sin_tramos"
                      ? "bg-rose-100 ring-2 ring-rose-500"
                      : "bg-rose-50 hover:bg-rose-100"
                  }`}
                >
                  <AlertCircle
                    className={`h-3.5 w-3.5 ${activeFilter === "sin_tramos" ? "text-rose-600" : "text-rose-500 animate-pulse"}`}
                  />
                  <div>
                    <div className="text-xs font-bold text-rose-600 leading-none">
                      {metrics.sinTramos}
                    </div>
                    <div className="text-[10px] text-rose-600/70">
                      Sin tramos
                    </div>
                  </div>
                </button>
              </>
            )}

            {/* Distribución exp/imp inline */}
            {tipoOperacion === "all" && (
              <>
                <div className="h-6 w-px bg-gray-300" />
                <div className="flex items-center gap-1.5">
                  <Building2 className="h-3 w-3 text-gray-400" />
                  <span className="text-[10px] font-semibold text-green-600">
                    {metrics.exportacion} exp
                  </span>
                  <span className="text-[10px] text-gray-300">·</span>
                  <span className="text-[10px] font-semibold text-blue-600">
                    {metrics.importacion} imp
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Controles de filtro derecha */}
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-lg p-0.5 shadow-sm">
              {[
                { value: "all", label: "Todos" },
                { value: "exportacion", label: "Exp" },
                { value: "importacion", label: "Imp" },
              ].map((op) => (
                <button
                  key={op.value}
                  onClick={() => applyTipoOp(op.value)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
                    tipoOperacion === op.value
                      ? "bg-gray-900 text-white shadow-sm"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {op.label}
                </button>
              ))}
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearAll}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-100 border border-gray-200 rounded-lg transition-all cursor-pointer"
              >
                <X className="h-3 w-3" />
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── KPI Cards por estado — compactas, en una sola fila ── */}
      <div className="grid grid-cols-3 gap-2">
        {kpis.map((kpi) => {
          const isActive = activeFilter === kpi.key;
          return (
            <button
              key={kpi.key}
              onClick={() => applyFilter(kpi.key)}
              className={`relative overflow-hidden rounded-lg border-2 px-3 py-2 transition-all duration-200 cursor-pointer text-left flex items-center gap-2.5 ${
                isActive
                  ? `${kpi.activeBg} ${kpi.border} shadow-md scale-[1.02]`
                  : "border-transparent bg-white hover:border-gray-200 hover:shadow-sm"
              }`}
            >
              {/* Icon */}
              <div
                className={`shrink-0 p-1.5 rounded-lg ${isActive ? "bg-white/20" : kpi.bg}`}
              >
                <span className={isActive ? "text-white" : kpi.color}>
                  {kpi.icon}
                </span>
              </div>

              {/* Text + bar */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <span
                    className={`text-xl font-black leading-none ${isActive ? "text-white" : "text-gray-900"}`}
                  >
                    {kpi.value}
                  </span>
                  {isActive && <Filter className="h-2.5 w-2.5 text-white/70" />}
                </div>
                <div
                  className={`text-[10px] font-medium truncate ${isActive ? "text-white/80" : "text-gray-500"}`}
                >
                  {kpi.label}
                </div>
                {/* Progress bar */}
                <div
                  className={`mt-1 h-1 rounded-full overflow-hidden ${isActive ? "bg-white/20" : "bg-gray-100"}`}
                >
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${isActive ? "bg-white" : kpi.bar}`}
                    style={{ width: `${kpi.pct}%` }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Chips secundarios ── */}
      {userRole !== "conductor" && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => applyFilter("con_tramos")}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all cursor-pointer ${
              activeFilter === "con_tramos"
                ? "bg-violet-600 border-violet-600 text-white shadow-sm"
                : "bg-white border-gray-200 text-gray-600 hover:border-violet-300"
            }`}
          >
            <Route className="h-3 w-3" />
            {metrics.conTramos} con tramos
          </button>

          {hasActiveFilters && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-gray-900 text-white">
              <Filter className="h-3 w-3" />
              Filtrado
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViajesDashboard;
